import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Lock } from "lucide-react";
import { GoogleRatingBadge } from "@/components/GoogleReviews";
import { TIMES, todayISO } from "@/pages/booking/bookingUi";
import { CustomerDetailsStep } from "@/pages/booking/CustomerDetailsStep";
import { ServicePickerStep } from "@/pages/booking/ServicePickerStep";
import { VehicleStep } from "@/pages/booking/VehicleStep";
import { SlotStep } from "@/pages/booking/SlotStep";
import { BookingSummary } from "@/pages/booking/BookingSummary";
import { BookingSuccess } from "@/pages/booking/BookingSuccess";
import { fireBookingConversion } from "@/lib/track";

export default function BookingPage() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const rebook = location.state?.rebook || null;
  const [submitted, setSubmitted] = useState(false);

  const initialService = params.get("service") || "";
  const paramDate = params.get("date");
  const initialDate = paramDate && /^\d{4}-\d{2}-\d{2}$/.test(paramDate) && paramDate >= todayISO() ? paramDate : todayISO();
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    service: initialService,
    services: initialService ? [initialService] : [],
    vehicle_make: rebook?.vehicle_make || "",
    vehicle_model: rebook?.vehicle_model || "",
    vehicle_year: rebook?.vehicle_year || new Date().getFullYear(),
    vehicle_rego: rebook?.vehicle_rego || "",
    vehicle_state: rebook?.vehicle_state || "TAS",
    preferred_date: initialDate,
    preferred_time: "09:00",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [fullSlots, setFullSlots] = useState([]);  // HH:MM values that are fully booked for the picked date
  const [prefillStatus, setPrefillStatus] = useState("loading"); // loading | filled | guest
  const [savedVehicles, setSavedVehicles] = useState([]);

  // Autofill from logged-in customer account (silently skipped for guests)
  useEffect(() => {
    api.get("/customer/prefill")
      .then((r) => {
        const d = r.data || {};
        setSavedVehicles(d.saved_vehicles || []);
        setForm((f) => ({
          ...f,
          customer_name: f.customer_name || d.name || "",
          customer_email: f.customer_email || d.email || "",
          customer_phone: f.customer_phone || d.phone || "",
          ...(rebook ? {} : {
            vehicle_make: f.vehicle_make || d.vehicle_make || "",
            vehicle_model: f.vehicle_model || d.vehicle_model || "",
            vehicle_year: d.vehicle_year || f.vehicle_year,
            vehicle_rego: f.vehicle_rego || d.vehicle_rego || "",
            vehicle_state: d.vehicle_state || f.vehicle_state,
          }),
        }));
        setPrefillStatus("filled");
      })
      .catch(() => setPrefillStatus("guest"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySavedVehicle = (v) => setForm((f) => ({
    ...f, vehicle_make: v.make, vehicle_model: v.model, vehicle_year: v.year,
    vehicle_rego: v.rego || "", vehicle_state: v.state || "TAS",
  }));

  // Refresh full-slot list whenever the picked date changes
  useEffect(() => {
    if (!form.preferred_date) return;
    api.get(`/appointments/slots`, { params: { date: form.preferred_date } })
      .then((r) => setFullSlots(r.data?.full_slots || []))
      .catch(() => setFullSlots([]));
  }, [form.preferred_date]);

  // If the currently-picked time becomes full (e.g. user changed date), auto-slide to next open one
  useEffect(() => {
    if (!fullSlots.includes(form.preferred_time)) return;
    const nextOpen = TIMES.find((t) => !fullSlots.includes(t));
    if (nextOpen) setForm((f) => ({ ...f, preferred_time: nextOpen }));
  }, [fullSlots, form.preferred_time]);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/categories")])
      .then(([sRes, cRes]) => {
        setServices(sRes.data);
        setCategories(cRes.data);
        if (!initialService && sRes.data.length) {
          setForm((f) => (f.services.length ? f : { ...f, services: [sRes.data[0].id] }));
        }
      })
      .catch(() => {});
  }, [initialService]);

  // When user picks a category, narrow chips and auto-pick the first matching service
  const filteredServices = useMemo(() => {
    if (activeCat === "all") return services;
    if (activeCat === "__other") {
      return services.filter((s) => !s.category || !categories.find((c) => c.name === s.category));
    }
    return services.filter((s) => s.category === activeCat);
  }, [services, categories, activeCat]);

  const catChips = useMemo(() => {
    const used = new Set(services.map((s) => (s.category || "").trim()).filter(Boolean));
    const orderedCats = categories.filter((c) => used.has(c.name)).map((c) => c.name);
    const hasOther = services.some((s) => !s.category || !categories.find((c) => c.name === s.category));
    const chips = [{ id: "all", label: "All" }, ...orderedCats.map((n) => ({ id: n, label: n }))];
    if (hasOther) chips.push({ id: "__other", label: "Other" });
    return chips;
  }, [services, categories]);

  // Multi-select spans categories, so no auto-swap when the filter changes.

  const submit = async (e) => {
    e.preventDefault();
    if (!form.services.length) {
      toast.error("Please select at least one service.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/appointments", {
        ...form,
        service: form.services[0],
        services: form.services,
        vehicle_year: Number(form.vehicle_year),
      });
      fireBookingConversion(form);
      toast.success("Booking received — we'll be in touch shortly.");
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 409) {
        // Slot filled up between page-load and submit — refresh availability + surface a clear error
        toast.error(err.response.data?.detail || "That time slot was just taken. Please pick another.");
        try {
          const r = await api.get(`/appointments/slots`, { params: { date: form.preferred_date } });
          setFullSlots(r.data?.full_slots || []);
        } catch (slotErr) {
          console.warn("Failed to refresh full slots after 409", slotErr);
        }
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to book");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <BookingSuccess
        form={form}
        onHome={() => navigate("/")}
        onBookAnother={() => {
          setSubmitted(false);
          setForm({ ...form, vehicle_make: "", vehicle_model: "", vehicle_rego: "", notes: "" });
          window.scrollTo({ top: 0 });
        }}
      />
    );
  }

  return (
    <div data-testid="booking-page" className="bg-background">
      <Toaster theme="dark" position="top-center" />

      <section className="border-b border-brand-red/20 bg-zinc-950/30 hud-grid-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-red blink-dot" />
            <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">Service booking · Slots live</span>
          </div>
          <h1 className="font-display uppercase text-5xl sm:text-6xl tracking-tighter text-white leading-[0.9]">
            Book in <span className="text-brand-red">60 seconds.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-300">
            No account needed. Fill in the form, we&apos;ll call you back to confirm.
          </p>
          <GoogleRatingBadge className="mt-6" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <form data-testid="booking-form" onSubmit={submit} className="lg:col-span-8 hud-panel rounded-xl p-10 space-y-8">
          <CustomerDetailsStep form={form} setForm={setForm} prefillStatus={prefillStatus} rebook={rebook} />
          <ServicePickerStep form={form} setForm={setForm} catChips={catChips} activeCat={activeCat} setActiveCat={setActiveCat} filteredServices={filteredServices} />
          <VehicleStep form={form} setForm={setForm} savedVehicles={savedVehicles} applySavedVehicle={applySavedVehicle} />
          <SlotStep form={form} setForm={setForm} fullSlots={fullSlots} />
          <div className="lg:hidden flex items-center justify-between border border-brand-red/30 bg-brand-red/5 px-4 py-3 rounded" data-testid="booking-mobile-total">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">
              {form.services.length} service{form.services.length === 1 ? "" : "s"} selected
            </span>
            <span className="font-display text-xl text-brand-red">
              from ${services.filter((s) => form.services.includes(s.id)).reduce((t, s) => t + (Number(s.price_from) || 0), 0)}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <button data-testid="booking-submit" disabled={submitting} className="glow-btn rounded w-full sm:w-auto px-10 py-4 bg-brand-red text-white font-bold uppercase tracking-wider hover:bg-[#B91C2B] disabled:opacity-60">
              {submitting ? "Booking…" : "Submit booking"}
            </button>
            <GoogleRatingBadge />
          </div>
        </form>

        <BookingSummary form={form} services={services} />
      </section>

      {/* Admin login portal */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div data-testid="admin-portal" className="hud-panel-silver rounded-xl p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 border border-brand-red/50 bg-brand-red/10 grid place-items-center">
              <Lock className="h-5 w-5 text-brand-red" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Workshop staff only</div>
              <h3 className="mt-1 font-display uppercase text-2xl text-white tracking-tight">Admin Portal</h3>
              <p className="mt-1 text-sm text-zinc-400 max-w-md">
                Sign in to manage bookings, edit service prices and descriptions, and view customer messages.
              </p>
            </div>
          </div>
          <Link
            data-testid="admin-portal-btn"
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white font-bold uppercase tracking-wider text-sm hover:bg-[#B91C2B] whitespace-nowrap"
          >
            <Lock className="h-4 w-4" /> Admin login
          </Link>
        </div>
      </section>

      <style>{`
        .ta-input { width:100%; background:#0a0a0a; border:1px solid #27272a; color:#fafafa; padding:12px 14px; font-family:'IBM Plex Sans',sans-serif; font-size:14px; outline:none; transition:border-color .15s; }
        .ta-input:focus { border-color:#E11D2B; box-shadow:0 0 0 1px #E11D2B; }
        .ta-input::placeholder { color:#52525b; }
      `}</style>
    </div>
  );
}
